import { useEffect } from 'react';
import CircularProgress from '@mui/material/CircularProgress';
import { useAtom, useSetAtom } from 'jotai';
import {
  useStructureForRiskAssessmentQuery,
  useAllAssemblyTemplatesForRiskAssessmentQuery,
  TemplateTypeEnum,
  useGetTemplateByTypeQuery,
} from '@/__generated__/graphql';
import * as state from '@/components/Analysis/state';
import { Wrapper, LoadingSpinnerContainer } from './styles';
import { selectedStructureId } from '@/state';
import {
  hasRiskAssessment,
  selectedStructureRiskAssessment,
  riskAssessmentAssemblyTemplate,
  structureTemplate,
} from '@/components/Analysis/state';
import { Snackbar } from '../../../shared/Snackbar';

interface RiskAssessmentDataLoaderProps {
  structureId: string;
  children: React.ReactNode;
}

export const RiskAssessmentDataLoader = ({
  structureId,
  children,
}: RiskAssessmentDataLoaderProps) => {
  const [snackbarMessage, setSnackbarMessage] = useAtom(state.snackbarMessage);
  const setHasRiskAssessment = useSetAtom(hasRiskAssessment);
  const setSelectedStructureRiskAssessment = useSetAtom(selectedStructureRiskAssessment);
  const setRiskAssessmentFilterTemplate = useSetAtom(riskAssessmentAssemblyTemplate);
  const setSelectedStructureId = useSetAtom(selectedStructureId);
  const setStructureTemplate = useSetAtom(structureTemplate);

  const fetchStructureQueryResult = useStructureForRiskAssessmentQuery({
    variables: {
      structureId,
    },
  });

  const { loading, data: filterTemplates } = useAllAssemblyTemplatesForRiskAssessmentQuery({
    variables: {
      structureId,
      type: TemplateTypeEnum.Assembly,
    },
    fetchPolicy: 'cache-and-network',
  });

  const fetchStructureTemplateQueryResult = useGetTemplateByTypeQuery({
    variables: {
      structureId: structureId,
      type: TemplateTypeEnum.Structure,
    },
  });

  useEffect(() => {
    setStructureTemplate(fetchStructureTemplateQueryResult?.data?.allTemplates[0]);
  }, [fetchStructureTemplateQueryResult?.data?.allTemplates, setStructureTemplate]);

  useEffect(() => {
    setSelectedStructureId(structureId);
  }, [setSelectedStructureId, structureId]);

  useEffect(() => {
    if (!loading && filterTemplates) {
      setRiskAssessmentFilterTemplate(filterTemplates.allTemplates[0]);
    }
  }, [filterTemplates, loading, setRiskAssessmentFilterTemplate]);

  useEffect(() => {
    const structure = fetchStructureQueryResult?.data?.structure;
    if (structure?.hasRiskAssessment) {
      setHasRiskAssessment(structure.hasRiskAssessment);
    }
    if (structure) {
      setSelectedStructureRiskAssessment(structure);
    }
  }, [
    fetchStructureQueryResult?.data?.structure,
    setHasRiskAssessment,
    setSelectedStructureRiskAssessment,
  ]);

  if (fetchStructureQueryResult.error) {
    return <div>Error While Fetching Structure...</div>;
  }

  const closeSnack = (_?: React.SyntheticEvent | Event, reason?: string) => {
    if (reason === 'clickaway') {
      return;
    }
    setSnackbarMessage({ shouldShow: false, content: undefined });
  };
  const { shouldShow, content } = snackbarMessage;

  if (fetchStructureQueryResult.loading) {
    return (
      <Wrapper>
        <LoadingSpinnerContainer>
          <CircularProgress size="10rem" />
        </LoadingSpinnerContainer>
        <Snackbar open={shouldShow} onClose={closeSnack}>
          {content}
        </Snackbar>
      </Wrapper>
    );
  }

  return <>{children}</>;
};
